import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Trophy } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { CompTag } from '@/components/CompTag';
import { ImgPlaceholder } from '@/components/ImgPlaceholder';
import { LoomliteMark } from '@/components/LoomliteMark';
import { supabase } from '@/lib/supabase';
import { useCompetition } from '@/hooks/useCompetitions';
import { cn, formatDate } from '@/lib/utils';

interface WinnerRow {
	Id: string;
	WinnerSlotId: string;
	SubmissionId: string;
	Blurb: string | null;
	slot: { Label: string; CategoryName: string | null; DisplayOrder: number } | null;
	submission: { Title: string; EntityType: string; SubmitterUserId: string } | null;
}

function WinnerCrown() {
	return (
		<div className="ll-trophy-glow inline-block">
			<svg width="56" height="56" viewBox="0 0 48 48" fill="none" aria-hidden="true">
				<defs>
					<linearGradient id="crown-g" x1="0" y1="0" x2="48" y2="48">
						<stop offset="0" stopColor="var(--color-loomlite-gold-soft)" />
						<stop offset="1" stopColor="var(--color-loomlite-gold)" />
					</linearGradient>
				</defs>
				<path
					d="M6 34 L10 14 L18 24 L24 10 L30 24 L38 14 L42 34 Z"
					fill="url(#crown-g)"
					stroke="var(--color-loomlite-gold-soft)"
					strokeWidth="1"
					strokeLinejoin="round"
				/>
				<rect x="6" y="34" width="36" height="5" rx="1" fill="url(#crown-g)" />
				<circle cx="10" cy="14" r="2" fill="var(--color-loomlite-purple)" />
				<circle cx="24" cy="10" r="2.2" fill="var(--color-loomlite-purple)" />
				<circle cx="38" cy="14" r="2" fill="var(--color-loomlite-purple)" />
			</svg>
		</div>
	);
}

function GrandWinnerCard({ winner }: { winner: WinnerRow }) {
	const title = winner.submission?.Title ?? 'Winning entry';
	const category = winner.submission?.EntityType;
	return (
		<div
			className="overflow-hidden rounded-xl border border-loomlite-gold/40 p-5"
			style={{
				background:
					'linear-gradient(180deg, rgba(255,140,0,0.06) 0%, transparent 50%), var(--color-loomlite-card)',
			}}
		>
			<ImgPlaceholder label={`${title.toLowerCase()} · 16:9`} height={260} tone="gold" />
			<div className="mt-[18px] flex gap-1.5">
				{category && (
					<CompTag color="purple" withDot={false}>
						{category}
					</CompTag>
				)}
				{winner.slot?.CategoryName && (
					<CompTag color="muted" withDot={false}>
						{winner.slot.CategoryName}
					</CompTag>
				)}
			</div>
			<h3 className="m-0 mt-2.5 font-heading text-[26px] font-semibold leading-tight tracking-tight">
				{title}
			</h3>
			{winner.Blurb && (
				<p className="m-0 mt-2.5 text-sm leading-relaxed text-loomlite-muted">{winner.Blurb}</p>
			)}
			<div className="mt-4 flex items-center gap-2.5 border-t border-loomlite-border pt-4">
				<div
					className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white"
					style={{
						background:
							'linear-gradient(135deg, var(--color-loomlite-purple) 0%, var(--color-loomlite-gold) 100%)',
					}}
					aria-hidden="true"
				>
					{(winner.submission?.Title ?? 'L').slice(0, 1).toUpperCase()}
				</div>
				<div>
					<div className="text-[13px] font-medium text-loomlite-text">
						{winner.slot?.Label ?? 'Grand winner'}
					</div>
					<div className="text-[11px] text-loomlite-dim">
						{winner.submission?.EntityType ?? 'Entry'} · Compendium laureate
					</div>
				</div>
			</div>
		</div>
	);
}

function JudgeQuote({ blurb, attribution }: { blurb: string; attribution?: string }) {
	return (
		<div className="rounded-xl border border-loomlite-purple/30 bg-loomlite-card p-5">
			<div className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-loomlite-purple-soft">
				From the judges
			</div>
			<p className="m-0 text-[15px] italic leading-relaxed text-loomlite-text">“{blurb}”</p>
			{attribution && <div className="mt-3 text-xs text-loomlite-muted">: {attribution}</div>}
		</div>
	);
}

function PodiumCard({ rank, winner }: { rank: number; winner: WinnerRow }) {
	const medalColor = rank === 1 ? '#FFB347' : rank === 2 ? '#C4CAD6' : '#B8733A';
	const title = winner.submission?.Title ?? 'Entry';
	const slotLabel =
		winner.slot?.Label ?? (rank === 1 ? 'Grand winner' : rank === 2 ? 'Runner-up' : 'Third place');
	return (
		<div
			className={cn(
				'relative rounded-xl border p-[18px]',
				rank === 1 ? 'border-loomlite-gold/40' : 'border-loomlite-border',
			)}
			style={{ background: 'var(--color-loomlite-card)' }}
		>
			<div
				className="absolute -top-3 left-[18px] flex h-8 w-8 items-center justify-center rounded-full font-heading text-sm font-bold text-loomlite-ink"
				style={{ background: medalColor, boxShadow: `0 0 20px ${medalColor}66` }}
			>
				{rank}
			</div>
			<div className="mt-2">
				<ImgPlaceholder
					label={title.toLowerCase()}
					height={130}
					tone={rank === 1 ? 'gold' : 'purple'}
				/>
			</div>
			<div className="mt-3">
				<div
					className="text-[10px] font-semibold uppercase tracking-[0.1em]"
					style={{ color: medalColor }}
				>
					{slotLabel}
				</div>
				<div className="mt-1 text-base font-semibold text-loomlite-text">{title}</div>
				{winner.submission?.EntityType && (
					<div className="mt-0.5 text-xs text-loomlite-muted">{winner.submission.EntityType}</div>
				)}
			</div>
		</div>
	);
}

export function AnnouncementPage() {
	const { slug } = useParams();
	const { data: competition, isLoading } = useCompetition(slug);

	const { data: winners } = useQuery({
		queryKey: ['competition-winners', competition?.Id],
		enabled: Boolean(competition?.Id),
		queryFn: async (): Promise<WinnerRow[]> => {
			const { data, error } = await supabase
				.from('CompetitionWinners')
				.select(
					'Id, WinnerSlotId, SubmissionId, Blurb, slot:CompetitionWinnerSlots(Label,CategoryName,DisplayOrder), submission:Submissions(Title,EntityType,SubmitterUserId)',
				)
				.eq('CompetitionId', competition!.Id);
			if (error) throw error;
			const rows = (data ?? []) as unknown as WinnerRow[];
			return rows.sort((a, b) => (a.slot?.DisplayOrder ?? 0) - (b.slot?.DisplayOrder ?? 0));
		},
	});

	if (isLoading) return <p className="p-8 text-loomlite-muted">Loading…</p>;
	if (!competition) return <Navigate to="/" replace />;
	if (competition.Status !== 'announced' && competition.Status !== 'archived')
		return <Navigate to={`/competitions/${slug}`} replace />;

	const usePodium = (winners?.length ?? 0) > 1;
	const [first, second, third] = winners ?? [];

	return (
		<div className="min-h-screen">
			<header className="border-b border-loomlite-border bg-loomlite-ink/50 backdrop-blur">
				<div className="mx-auto flex max-w-6xl items-center justify-between px-8 py-3.5">
					<LoomliteMark size={26} />
					<CompTag color="gold">{competition.Name} · Results</CompTag>
				</div>
			</header>

			<div
				className="px-8 pb-8 pt-14 text-center"
				style={{
					background:
						'radial-gradient(600px 300px at 50% 0%, rgba(255,140,0,0.15), transparent 70%)',
				}}
			>
				<WinnerCrown />
				{competition.AnnouncementAt && (
					<div className="mt-4">
						<CompTag color="gold">Announced · {formatDate(competition.AnnouncementAt)}</CompTag>
					</div>
				)}
				<h1 className="m-0 mt-5 font-heading text-[56px] font-bold leading-[1.02] tracking-[-0.03em]">
					The {usePodium ? 'podium' : 'grand winner'} of the
					<br />
					<span className="ll-gradient-text">{competition.Name}</span>
				</h1>
				<p className="m-0 mx-auto mt-3 max-w-[560px] text-[15px] text-loomlite-muted">
					Thank you to every table who submitted. Each entry was scored on originality, craft, and
					table usefulness.
				</p>
			</div>

			<main className="mx-auto max-w-6xl px-8 pb-16">
				{(winners?.length ?? 0) === 0 && (
					<p className="text-loomlite-muted">Winners haven't been posted yet.</p>
				)}

				{!usePodium && first && (
					<div className="grid grid-cols-1 items-start gap-8 pt-8 lg:grid-cols-[1.2fr_1fr]">
						<GrandWinnerCard winner={first} />
						<aside className="flex flex-col gap-4">
							<div className="rounded-xl border border-loomlite-gold/40 bg-loomlite-card p-5">
								<div className="flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-loomlite-gold-soft">
									<Trophy className="h-4 w-4" /> Prize awarded
								</div>
								<div className="mt-2.5 text-lg font-semibold text-loomlite-text">
									{first.slot?.Label ?? 'Grand winner'}
								</div>
								<ul className="m-0 mt-3.5 flex list-none flex-col gap-2 p-0 text-[13px] text-loomlite-muted">
									<li className="flex gap-2">
										<Check
											className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-loomlite-green"
											strokeWidth={3}
										/>
										Featured placement on the Loomlite results page
									</li>
									<li className="flex gap-2">
										<Check
											className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-loomlite-green"
											strokeWidth={3}
										/>
										Permanent “Laureate” profile frame
									</li>
									<li className="flex gap-2">
										<Check
											className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-loomlite-green"
											strokeWidth={3}
										/>
										Highlighted across Loomlite for the season
									</li>
								</ul>
							</div>
							{first.Blurb && (
								<JudgeQuote blurb={first.Blurb} attribution="Compendium guest judges" />
							)}
							<div className="flex gap-2.5">
								<Button asChild>
									<Link to={`/competitions/${slug}`}>Open in Loomlite</Link>
								</Button>
								<Button asChild variant="outline">
									<Link to="/">See all competitions</Link>
								</Button>
							</div>
						</aside>
					</div>
				)}

				{usePodium && (
					<div className="pt-10">
						<div className="grid grid-cols-1 items-end gap-6 md:grid-cols-3">
							<div className="md:translate-y-6">
								{second && <PodiumCard rank={2} winner={second} />}
							</div>
							<div>{first && <PodiumCard rank={1} winner={first} />}</div>
							<div className="md:translate-y-11">
								{third && <PodiumCard rank={3} winner={third} />}
							</div>
						</div>

						{first && (
							<div
								className={cn(
									'mt-14 grid grid-cols-1 gap-6',
									first.Blurb && 'lg:grid-cols-[1.4fr_1fr]',
								)}
							>
								{first.Blurb && (
									<JudgeQuote blurb={first.Blurb} attribution="Compendium guest judges" />
								)}
								<div className="rounded-xl border border-loomlite-gold/40 bg-loomlite-card p-5">
									<div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-loomlite-gold-soft">
										<Trophy className="h-4 w-4" /> Grand prize ·{' '}
										{first.submission?.Title ?? 'winning entry'}
									</div>
									<div className="mt-2 text-base font-semibold text-loomlite-text">
										{first.slot?.Label ?? 'Grand winner'}
									</div>
									<div className="mt-1 text-xs text-loomlite-muted">
										Runners-up receive featured placement and a profile frame.
									</div>
								</div>
							</div>
						)}

						{(winners?.length ?? 0) > 3 && (
							<section className="mt-14 space-y-3">
								<h3 className="font-heading text-lg font-semibold">Other winners</h3>
								<ul className="divide-y divide-loomlite-border rounded-xl border border-loomlite-border bg-loomlite-card">
									{winners!.slice(3).map((w) => (
										<li key={w.Id} className="flex items-start justify-between gap-4 px-5 py-3.5">
											<div>
												<div className="text-sm font-medium text-loomlite-text">
													{w.submission?.Title ?? 'Entry'}
												</div>
												{w.slot?.Label && (
													<div className="mt-0.5 text-xs text-loomlite-muted">{w.slot.Label}</div>
												)}
											</div>
											{w.submission?.EntityType && (
												<CompTag color="muted" withDot={false}>
													{w.submission.EntityType}
												</CompTag>
											)}
										</li>
									))}
								</ul>
							</section>
						)}

						<div className="mt-10 flex gap-2.5">
							<Button asChild>
								<Link to={`/competitions/${slug}`}>Back to competition</Link>
							</Button>
							<Button asChild variant="outline">
								<Link to="/">See all competitions</Link>
							</Button>
						</div>
					</div>
				)}

				<div className="mt-10">
					<Button variant="ghost" size="sm" asChild>
						<Link to={`/competitions/${slug}`}>
							<ArrowLeft className="mr-1 h-4 w-4" /> Back to competition
						</Link>
					</Button>
				</div>
			</main>
		</div>
	);
}
